import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import type {
  CreateRateRequestPayload,
  MaxRateRequestRow,
  RateRequest,
  RateRequestDecisionPayload,
} from '@typeDefinitions/rateRequestTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';

import { buildSourceTrackingConfig } from './serviceHelpers';

const postRateRequest = async <T>(
  url: string,
  payload: T,
  fallback: string,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<RateRequest> => {
  const response: V4ApiResponse<RateRequest> = await apiClient.post(
    url,
    payload,
    buildSourceTrackingConfig(changeTrackingSource),
  );
  if (response.isFailure) {
    throw new Error(response.errors[0]?.message ?? fallback);
  }
  return response.value;
};

export async function requestMaxRateIncrease(
  payload: CreateRateRequestPayload,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<RateRequest> {
  const fallback = 'Failed to request max rate increase';
  try {
    return await postRateRequest(
      '/Load/RequestMaxRateIncrease',
      payload,
      fallback,
      changeTrackingSource,
    );
  } catch (error: unknown) {
    logError('requestMaxRateIncrease', error as Error);
    throw createError(error, fallback);
  }
}

export async function approveRateRequest(
  payload: RateRequestDecisionPayload,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<RateRequest> {
  const fallback = 'Failed to approve max rate increase';
  try {
    return await postRateRequest(
      '/Load/ApproveRateRequest',
      payload,
      fallback,
      changeTrackingSource,
    );
  } catch (error: unknown) {
    logError('approveRateRequest', error as Error);
    throw createError(error, fallback);
  }
}

export async function denyRateRequest(
  payload: RateRequestDecisionPayload,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<RateRequest> {
  const fallback = 'Failed to deny max rate increase';
  try {
    return await postRateRequest(
      '/Load/DenyRateRequest',
      payload,
      fallback,
      changeTrackingSource,
    );
  } catch (error: unknown) {
    logError('denyRateRequest', error as Error);
    throw createError(error, fallback);
  }
}

export async function fetchMaxRateRequests(): Promise<MaxRateRequestRow[]> {
  const fallback = 'Failed to get max rate requests';
  try {
    const response: V4ApiResponse<MaxRateRequestRow[]> = await apiClient.get(
      '/Queue/maxraterequests',
    );
    if (response.isFailure) {
      throw new Error(response.errors[0]?.message ?? fallback);
    }
    return response.value;
  } catch (error: unknown) {
    logError('fetchMaxRateRequests', error as Error);
    throw createError(error, fallback);
  }
}
